import { eveningChipText, feedbackChipText, inboundChipText, painChipText, variableChipText, variableMovementLine, type EveningNoteSummary, type FeedbackSummary, type InboundSummary, type PainEventsSummary, type VariableReadingsSummary } from '@/lib/people-data';
import s from '@/pages/console/console.module.css';

/**
 * The small counts a People row carries about what a person wrote or told
 * us (pv-auth #336 inbound, #334 product feedback, #329 evening notes) and
 * the red flag for "stop, this hurts" (#337 PainEvent), which is drawn
 * first because it is the one that cannot wait.
 * Counts only: the words live on the person page, where opening them is a
 * deliberate act the access log records. A chip at zero is not drawn at all,
 * so a row with nothing says nothing rather than "0 unread".
 */
export function InboundChip({ inbound }: { inbound: InboundSummary | null | undefined }) {
  const text = inboundChipText(inbound);
  if (!text) return null;
  const unread = (inbound?.unread ?? 0) > 0;
  return <span className={`${s.chip} ${unread ? s.chipUnread : ''}`} data-chip="inbound" data-unread={unread ? '1' : '0'} title="What they wrote us through GHL">✉ {text}</span>;
}

export function FeedbackChip({ productFeedback }: { productFeedback: FeedbackSummary | null | undefined }) {
  const text = feedbackChipText(productFeedback);
  if (!text) return null;
  return <span className={s.chip} data-chip="feedback" title="What they told us about the product">{text}</span>;
}

export function EveningChip({ eveningNote }: { eveningNote: EveningNoteSummary | null | undefined }) {
  const text = eveningChipText(eveningNote);
  if (!text) return null;
  return <span className={s.chip} data-chip="evening" title="Evening voice notes">{text}</span>;
}

/** The red flag: they said it hurt and stopped. Count and nothing else; the words are on the person page. */
export function PainChip({ painEvents }: { painEvents: PainEventsSummary | null | undefined }) {
  const text = painChipText(painEvents);
  if (!text) return null;
  return <span className={`${s.chip} ${s.chipFlag}`} data-chip="pain" title="Said it hurt and stopped; the flagged lines are on the person page">⚑ {text}</span>;
}

/** Their movement readings are all over the place (pv-auth #452): which movements; the numbers are in the title and on the person page. */
export function VariableChip({ variableReadings }: { variableReadings: VariableReadingsSummary | null | undefined }) {
  const text = variableChipText(variableReadings);
  if (!text) return null;
  return (
    <span className={`${s.chip} ${s.chipFlag}`} data-chip="variable" title={variableReadings!.movements.map(variableMovementLine).join('\n')}>
      ≈ {text}
    </span>
  );
}

/** All of them on one line, the flags first; nothing at all when every count is zero. */
export function PersonChips({ p }: { p: { inbound?: InboundSummary | null; productFeedback?: FeedbackSummary | null; eveningNote?: EveningNoteSummary | null; painEvents?: PainEventsSummary | null; variableReadings?: VariableReadingsSummary | null } }) {
  if (!inboundChipText(p.inbound) && !feedbackChipText(p.productFeedback) && !eveningChipText(p.eveningNote) && !painChipText(p.painEvents) && !variableChipText(p.variableReadings)) return null;
  return (
    <div className={s.chips} data-panel="person-chips">
      <PainChip painEvents={p.painEvents} />
      <VariableChip variableReadings={p.variableReadings} />
      <InboundChip inbound={p.inbound} />
      <FeedbackChip productFeedback={p.productFeedback} />
      <EveningChip eveningNote={p.eveningNote} />
    </div>
  );
}
